import { useLanguage } from "@/hooks/useLanguage";
import { interpolate } from "@/lib/localizedContent";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { RotateCcw, ArrowRight, Package, ShoppingCart, Loader2, Check, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import PriceDisplay from "@/components/PriceDisplay";
import { getCartItems, setCartItems as saveCartItems } from "@/lib/sessionUtils";
import { useCollectionCart } from "@/hooks/useCollectionCart";
import { useAuth } from "@/hooks/useAuth";

interface ReorderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefillCode?: string;
  prefillOrderId?: string;
}

interface CartItem {
  id: string;
  type: string;
  perfume: {
    id: string;
    name: string;
    type: string;
    // Customer-facing code (shown in cart)
    code: string;
    // Stored for admin order details
    perfumeCode?: string;
    anayaCode?: string;
    description: string;
    topNotes: string[];
    heartNotes: string[];
    baseNotes: string[];
    color: string;
    occasion: string;
    matchPercentage?: number;
    narration?: string;
  };
  perfumes: any[];
  recipientName: string;
  country: string;
  quantity: number;
  price: number;
  isReorder: boolean;
  packagingOptionId?: string;
  quizData?: any;
}

interface ReorderOption {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  image_url: string | null;
}

interface PerfumeData {
  id: string;
  name: string;
  type: string;
  anayaCode?: string;
  code?: string;
  perfumeCode?: string;
  topNotes?: string[];
  middleNotes?: string[];
  heartNotes?: string[];
  baseNotes?: string[];
  narration?: string;
  occasion?: string;
  matchPercentage?: number;
}

interface OrderData {
  id: string;
  system_perfume_code: string;
  anaya_perfume_code: string | null;
  perfume_description: string | null;
  recipient_name: string | null;
  delivery_address: { country?: string } | null;
  quiz_selections: {
    recipientName?: string;
    perfumes?: PerfumeData[];
    selectedPerfumeIds?: string[];
    packageType?: string;
    gender?: string;
    ageBracket?: string;
    skinType?: string;
    nature?: string;
    personality?: string;
    style?: string;
    collectionItems?: CollectionItemData[];
  } | null;
}

interface CollectionItemData {
  productId: string;
  productName: string;
  productSlug?: string;
  productImage?: string;
  sizeMl: number;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  sku?: string;
  perfumeCode?: string;
  isSample?: boolean;
  isSignatureBox?: boolean;
  boxContents?: Array<{
    productId: string;
    productName: string;
    perfumeCode?: string;
    sku?: string;
    sizeMl: number;
  }>;
}

// Direct collection product match (found by SKU/perfume_code)
interface DirectCollectionProduct {
  id: string;
  name: string;
  slug: string;
  sku: string | null;
  perfume_code: string | null;
  price_aed: number;
  price_50ml: number | null;
  price_100ml: number | null;
  is_signature_box: boolean;
  is_50ml_active: boolean;
  is_100ml_active: boolean;
  images?: Array<{ image_url: string; is_primary: boolean }>;
  boxItems?: Array<{
    perfume_name: string;
    perfume_code: string | null;
    size_ml: number;
  }>;
}

const ReorderModal = ({ open, onOpenChange, prefillCode, prefillOrderId }: ReorderModalProps) => {
  const { t, isRTL, language, getLocalizedField } = useLanguage();
// Map perfume type to display label
const getTypeDisplayLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    'sensation': t("order.sensation_formal"),
    'fresh_energy': t("order.fresh_energy_outdoor"),
    'casual': t("order.casual_everyday"),
  };
  return typeLabels[type] || type;
};

  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { addToCart: addToCollectionCart } = useCollectionCart();
  const [perfumeCode, setPerfumeCode] = useState(prefillCode || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [reorderOptions, setReorderOptions] = useState<ReorderOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  
  // Order and perfume selection state
  const [foundOrder, setFoundOrder] = useState<OrderData | null>(null);
  const [selectedPerfumeIds, setSelectedPerfumeIds] = useState<string[]>([]);
  const [step, setStep] = useState<'code' | 'selection'>('code');

  // Collection order state
  const [isCollectionOrder, setIsCollectionOrder] = useState(false);
  const [selectedCollectionItemIds, setSelectedCollectionItemIds] = useState<string[]>([]);
  const [addingCollectionItems, setAddingCollectionItems] = useState(false);
  
  // Direct collection product match state (when user enters a product SKU/code)
  const [directProduct, setDirectProduct] = useState<DirectCollectionProduct | null>(null);
  const [directProductSize, setDirectProductSize] = useState<number>(100);

  // Fetch reorder packaging options from database
  useEffect(() => {
    const fetchReorderOptions = async () => {
      setLoadingOptions(true);
      
      // First try to get reorder category options
      let { data, error } = await supabase
        .from('packaging_options')
        .select('id, name, description, price, currency, image_url')
        .eq('category', 'reorder')
        .eq('is_active', true)
        .order('display_order');

      // If no reorder options, fall back to box category
      if (!data || data.length === 0) {
        const boxResult = await supabase
          .from('packaging_options')
          .select('id, name, description, price, currency, image_url')
          .eq('category', 'box')
          .eq('is_active', true)
          .order('display_order');
        
        data = boxResult.data;
        error = boxResult.error;
      }

      if (error) {
        console.error("Failed to load reorder options:", error);
      } else if (data && data.length > 0) {
        setReorderOptions(data);
      }
      setLoadingOptions(false);
    };

    if (open) {
      fetchReorderOptions();
    }
  }, [open]);

  // Update prefillCode when it changes and auto-search
  useEffect(() => {
    if (prefillCode && open) {
      setPerfumeCode(prefillCode);
      // Auto-trigger search when modal opens with prefillCode
      autoSearchCode(prefillCode);
    }
  }, [prefillCode, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('code');
      setFoundOrder(null);
      setSelectedPerfumeIds([]);
      setIsCollectionOrder(false);
      setSelectedCollectionItemIds([]);
      setDirectProduct(null);
      setDirectProductSize(100);
      setError("");
      setPerfumeCode("");
    }
  }, [open]);

  // Search collection_products by SKU or perfume_code
  const searchCollectionProduct = async (code: string): Promise<DirectCollectionProduct | null> => {
    const upperCode = code.trim().toUpperCase();
    
    const { data, error } = await supabase
      .from('collection_products')
      .select(`
        id, name, slug, sku, perfume_code, price_aed, price_50ml, price_100ml,
        is_signature_box, is_50ml_active, is_100ml_active,
        images:collection_product_images(image_url, is_primary)
      `)
      .eq('is_active', true)
      .or(`sku.ilike.${upperCode},perfume_code.ilike.${upperCode}`)
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    
    const product = data as any as DirectCollectionProduct;
    
    // If it's a signature box, fetch its contents
    if (product.is_signature_box) {
      const { data: boxItems } = await supabase
        .rpc('get_signature_box_items', { p_box_product_id: product.id });
      
      if (boxItems) {
        product.boxItems = (boxItems as any[]).map(bi => ({
          perfume_name: bi.perfume_name,
          perfume_code: bi.perfume_code,
          size_ml: bi.size_ml,
        }));
      }
    }
    
    return product;
  };

  // Unified search: tries order codes first, then collection product codes
  const performSearch = async (code: string) => {
    const upperCode = code.trim().toUpperCase();
    if (!upperCode) return;
    
    setIsLoading(true);
    setError("");
    setDirectProduct(null);

    try {
      // 1. Try order system_perfume_code
      const { data: orderData } = await supabase
        .from("orders")
        .select("*")
        .eq("system_perfume_code", upperCode)
        .limit(1)
        .maybeSingle();

      if (orderData) {
        handleOrderFound(orderData);
        return;
      }

      // 2. Try anaya_perfume_code
      const { data: fallbackData } = await supabase
        .from("orders")
        .select("*")
        .eq("anaya_perfume_code", upperCode)
        .limit(1)
        .maybeSingle();

      if (fallbackData) {
        handleOrderFound(fallbackData);
        return;
      }

      // 3. Try collection product SKU/perfume_code
      const collectionProduct = await searchCollectionProduct(upperCode);
      if (collectionProduct) {
        setDirectProduct(collectionProduct);
        setDirectProductSize(collectionProduct.is_100ml_active ? 100 : 50);
        setIsLoading(false);
        setStep('selection');
        return;
      }

      // 4. Try text search in quiz_selections (individual perfume codes)
      const { data: perfumeCodeOrders } = await supabase
        .from("orders")
        .select("*")
        .textSearch("quiz_selections", upperCode)
        .limit(1)
        .maybeSingle();
        
      if (perfumeCodeOrders) {
        handleOrderFound(perfumeCodeOrders);
        return;
      }

      setError(t("order.no_order_or_product_found_with_this_code_please_check_and_try_again"));
      setIsLoading(false);
    } catch (err) {
      setError(t("order.an_error_occurred_please_try_again"));
      setIsLoading(false);
    }
  };

  // Auto-search function (separate from form submit)
  const autoSearchCode = async (code: string) => {
    await performSearch(code);
  };

  // Get single bottle option (for single perfume reorders)
  const getSingleOption = () => {
    return reorderOptions.find(o => 
      o.name.toLowerCase().includes('single') || 
      o.name.toLowerCase().includes('100ml')
    ) || reorderOptions[0];
  };

  // Get box option (for multiple perfume reorders)
  const getBoxOption = () => {
    return reorderOptions.find(o => 
      o.name.toLowerCase().includes('box') || 
      o.name.toLowerCase().includes('signature')
    ) || reorderOptions[1] || reorderOptions[0];
  };

  const handleSearchCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!perfumeCode.trim()) {
      setError(t("order.please_enter_your_item_code"));
      return;
    }
    await performSearch(perfumeCode);
  };

  const handleOrderFound = (orderData: any) => {
    const quizSelections = orderData.quiz_selections as OrderData['quiz_selections'];
    const perfumes = quizSelections?.perfumes || [];
    const collectionItems = quizSelections?.collectionItems || [];
    
    setFoundOrder(orderData as OrderData);
    setIsLoading(false);
    
    // Check if this is a collection order
    if (collectionItems.length > 0) {
      setIsCollectionOrder(true);
      // Pre-select all collection items
      setSelectedCollectionItemIds(collectionItems.map((item: CollectionItemData) => item.productId));
      setStep('selection');
      return;
    }
    
    // Regular quiz order
    setIsCollectionOrder(false);
    // Always show selection step to display price and allow confirmation
    // Pre-select all perfumes
    setSelectedPerfumeIds(perfumes.length > 0 ? perfumes.map((p: PerfumeData) => p.id) : ['single']);
    setStep('selection');
  };

  const togglePerfumeSelection = (perfumeId: string) => {
    setSelectedPerfumeIds(prev => {
      if (prev.includes(perfumeId)) {
        // Don't allow deselecting if only one is selected
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== perfumeId);
      } else {
        return [...prev, perfumeId];
      }
    });
  };

  const addToCartDirect = (orderData: any, perfumeIdsToAdd: string[]) => {
    const quizSelections = orderData.quiz_selections as OrderData['quiz_selections'];
    const allPerfumes = quizSelections?.perfumes || [];
    
    // Get only selected perfumes
    const selectedPerfumes = perfumeIdsToAdd.length > 0
      ? allPerfumes.filter((p: PerfumeData) => perfumeIdsToAdd.includes(p.id))
      : allPerfumes;

    // Fixed pricing: AED 199 per perfume
    const PRICE_PER_PERFUME = 199;
    const isMultiplePerfumes = selectedPerfumes.length > 1;
    const packagingOption = isMultiplePerfumes ? getBoxOption() : getSingleOption();
    const price = selectedPerfumes.length * PRICE_PER_PERFUME;

    // Build quizData for reorder - preserve original quiz selections plus perfumes with all their codes
    const reorderQuizData = {
      recipientName: orderData.recipient_name || quizSelections?.recipientName || "Valued Customer",
      country: orderData.delivery_address?.country || "UAE",
      gender: quizSelections?.gender || "unisex",
      ageBracket: quizSelections?.ageBracket || "",
      skinType: quizSelections?.skinType || "",
      nature: quizSelections?.nature || "",
      personality: quizSelections?.personality || "",
      style: quizSelections?.style || "",
      // Mark as reorder so we know this is from an existing order
      isReorder: true,
      originalOrderCode: orderData.system_perfume_code,
    };

    // Create cart item with quizData to ensure codes are saved to database
    const reorderItem: CartItem = {
      id: `reorder-${Date.now()}`,
      type: isMultiplePerfumes ? "box" : "single",
      perfume: selectedPerfumes[0] ? {
        id: selectedPerfumes[0].id,
        name: selectedPerfumes[0].name || "Reorder Perfume",
        type: selectedPerfumes[0].type || "personalized",
        // Customer-facing code (never show Anaya code in cart)
        code: selectedPerfumes[0].perfumeCode || orderData.system_perfume_code,
        perfumeCode: selectedPerfumes[0].perfumeCode || orderData.system_perfume_code,
        // Internal reference for admin order details - AI-selected Anaya code
        anayaCode: selectedPerfumes[0].anayaCode || selectedPerfumes[0].code,
        description: orderData.perfume_description || "Your personalized fragrance",
        topNotes: selectedPerfumes[0].topNotes || [],
        heartNotes: selectedPerfumes[0].middleNotes || selectedPerfumes[0].heartNotes || [],
        baseNotes: selectedPerfumes[0].baseNotes || [],
        color: "burgundy",
        occasion: selectedPerfumes[0].occasion || "Your special moments",
        matchPercentage: selectedPerfumes[0].matchPercentage,
      } : {
        id: "reorder",
        name: "Reorder Perfume",
        type: "personalized",
        code: orderData.system_perfume_code,
        perfumeCode: orderData.system_perfume_code,
        anayaCode: undefined,
        description: orderData.perfume_description || "Your personalized fragrance",
        topNotes: [],
        heartNotes: [],
        baseNotes: [],
        color: "burgundy",
        occasion: "Your special moments",
      },
      perfumes: selectedPerfumes.map((p: PerfumeData) => ({
        id: p.id,
        name: p.name,
        type: p.type,
        // Customer-facing code - system generated AN code
        code: p.perfumeCode || orderData.system_perfume_code,
        perfumeCode: p.perfumeCode || orderData.system_perfume_code,
        // Internal admin code - AI-selected Anaya code from prefixed list
        anayaCode: p.anayaCode || p.code,
        description: p.narration || orderData.perfume_description,
        topNotes: p.topNotes || [],
        heartNotes: p.middleNotes || p.heartNotes || [],
        baseNotes: p.baseNotes || [],
        color: "burgundy",
        occasion: p.occasion,
        matchPercentage: p.matchPercentage,
        narration: p.narration,
      })),
      recipientName: orderData.recipient_name || quizSelections?.recipientName || "Valued Customer",
      country: orderData.delivery_address?.country || "UAE",
      quantity: 1,
      price: price,
      isReorder: true,
      packagingOptionId: packagingOption?.id,
      // CRITICAL: Include quizData so perfume codes are saved to database
      quizData: reorderQuizData as any,
    };

    // Add to cart (using localStorage)
    const existingCart = getCartItems();
    existingCart.push(reorderItem);
    saveCartItems(existingCart);

    // Trigger storage event for header to update cart count
    window.dispatchEvent(new Event("storage"));

    const itemCount = selectedPerfumes.length;
    const itemType = itemCount > 1 ? `box (${itemCount} perfumes)` : "100ml bottle";
    toast({
      title: t("order.added_to_cart"),
      description: interpolate(t("order.your_perfume_has_been_added_to_your_cart"), {  }),
    });

    // Close modal and navigate to checkout
    onOpenChange(false);
    setPerfumeCode("");
    navigate("/checkout");
  };

  const handleAddSelectedToCart = () => {
    // Direct collection product flow (searched by SKU/perfume_code)
    if (directProduct) {
      handleAddDirectProductToCart();
      return;
    }
    
    if (!foundOrder) return;
    
    // Collection order flow
    if (isCollectionOrder) {
      handleAddCollectionItemsToCart();
      return;
    }
    
    // For orders without perfume data, pass empty array to use fallback logic
    const idsToAdd = orderPerfumes.length === 0 ? [] : selectedPerfumeIds;
    addToCartDirect(foundOrder, idsToAdd);
  };

  // Add direct collection product to cart (found by SKU/code)
  const handleAddDirectProductToCart = async () => {
    if (!directProduct || !user) {
      toast({
        title: t("order.sign_in_required"),
        description: 'Please sign in to reorder collection items.',
        variant: 'destructive',
      });
      return;
    }

    setAddingCollectionItems(true);
    try {
      const sizeMl = directProduct.is_signature_box ? 100 : directProductSize;
      await addToCollectionCart(directProduct.id, 1, sizeMl, false);

      toast({
        title: t("order.added_to_cart"),
        description: interpolate(t("order.name_has_been_added_to_your_cart"), { name: directProduct.name }),
      });

      onOpenChange(false);
      setPerfumeCode("");
      navigate("/checkout");
    } catch (err) {
      toast({
        title: t("order.error"),
        description: 'Failed to add item to cart. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAddingCollectionItems(false);
    }
  };

  // Toggle collection item selection
  const toggleCollectionItemSelection = (productId: string) => {
    setSelectedCollectionItemIds(prev => {
      if (prev.includes(productId)) {
        if (prev.length === 1) return prev; // At least one must stay selected
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  // Add collection items to DB cart
  const handleAddCollectionItemsToCart = async () => {
    if (!foundOrder || !user) {
      toast({
        title: t("order.sign_in_required"),
        description: 'Please sign in to reorder collection items.',
        variant: 'destructive',
      });
      return;
    }

    const collectionItems = foundOrder.quiz_selections?.collectionItems || [];
    const selectedItems = collectionItems.filter(item => selectedCollectionItemIds.includes(item.productId));

    if (selectedItems.length === 0) return;

    setAddingCollectionItems(true);

    try {
      for (const item of selectedItems) {
        if (item.isSignatureBox && item.boxContents) {
          await addToCollectionCart(item.productId, item.quantity, item.sizeMl, false);
        } else {
          await addToCollectionCart(item.productId, item.quantity, item.sizeMl, item.isSample || false);
        }
      }

      toast({
        title: t("order.added_to_cart"),
        description: interpolate(t("order.count_items_added_to_your_cart"), { count: selectedItems.length }),
      });

      onOpenChange(false);
      setPerfumeCode("");
      navigate("/checkout");
    } catch (err) {
      toast({
        title: t("order.error"),
        description: 'Failed to add items to cart. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAddingCollectionItems(false);
    }
  };

  const handleClose = () => {
    setPerfumeCode("");
    setError("");
    setStep('code');
    setFoundOrder(null);
    setSelectedPerfumeIds([]);
    setIsCollectionOrder(false);
    setSelectedCollectionItemIds([]);
    setDirectProduct(null);
    setDirectProductSize(100);
    onOpenChange(false);
  };

  const singleOption = getSingleOption();
  const boxOption = getBoxOption();

  // Get perfumes from found order
  const orderPerfumes = foundOrder?.quiz_selections?.perfumes || [];
  
  // Collection items from found order
  const collectionItems = foundOrder?.quiz_selections?.collectionItems || [];
  
  // Fixed pricing: AED 199 per perfume
  const PRICE_PER_PERFUME = 199;
  // If no perfumes in quiz_selections (older orders), count as 1 perfume
  const effectiveCount = orderPerfumes.length === 0 ? 1 : selectedPerfumeIds.length;
  const currentPrice = effectiveCount * PRICE_PER_PERFUME;

  // Collection order pricing
  const selectedCollectionItems = collectionItems.filter(item => selectedCollectionItemIds.includes(item.productId));
  const collectionTotal = selectedCollectionItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  // Direct product pricing
  const directProductPrice = directProduct
    ? directProduct.is_signature_box
      ? directProduct.price_aed
      : directProductSize === 50
        ? (directProduct.price_50ml || directProduct.price_aed)
        : (directProduct.price_100ml || directProduct.price_aed)
    : 0;

  // Detect code type based on prefix (use found order's code when available for accuracy)
  const codeToCheck = foundOrder?.system_perfume_code || perfumeCode.trim().toUpperCase();
  const isCollectionCode = codeToCheck.startsWith('SC');
  const isIndividualCode = codeToCheck.startsWith('AN');

  // Determine the title and description for the dialog
  const dialogTitle = directProduct
    ? interpolate(t("order.re_order_name"), { name: directProduct.name })
    : isCollectionOrder
      ? t("order.re_order_your_collection")
      : t("order.re_order_your_perfume");

  const dialogDescription = step === 'code'
    ? t("order.enter_your_item_code_to_quickly_re_order")
    : directProduct
      ? interpolate(t("order.confirm_your_reorder_for_name"), { name: directProduct.name })
      : isCollectionOrder
        ? t("order.select_the_collection_items_you_d_like_to_reorder")
        : isCollectionCode
          ? t("order.this_is_a_collection_code_select_one_or_more_perfumes_from_your_original_order")
          : t("order.confirm_your_perfume_reorder");

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl">
            <RotateCcw className="h-5 w-5 text-gold" />
            {dialogTitle}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        {step === 'code' ? (
          <form onSubmit={handleSearchCode} className="space-y-6 mt-4">
            {/* Info Box */}
            <div className="bg-gold/10 border border-gold/20 rounded-lg p-4 flex items-start gap-3">
              <Package className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">{t("order.quick_reorder")}</p>
                <p className="text-muted-foreground">
                  {t("order.your_personalized_fragrance_will_be_crafted_exactly_as_before")}
                </p>
                {loadingOptions ? (
                  <div className="mt-2 flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-xs">{t("order.loading")}</span>
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gold font-semibold">AED 199</span>
                      <span className="text-xs text-muted-foreground">{t("order.per_perfume")}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Perfume Code Input */}
            <div className="space-y-2">
              <Label htmlFor="perfumeCode" className="text-foreground">
                {t("order.perfume_code")}
              </Label>
              <Input
                dir="ltr"
                id="perfumeCode"
                type="text"
                placeholder={t("order.e_g_an260101_or_sc260101")}
                value={perfumeCode}
                onChange={(e) => {
                  setPerfumeCode(e.target.value.toUpperCase());
                  setError("");
                }}
                className="text-center text-lg tracking-wider font-mono uppercase"
                autoComplete="off"
                autoFocus
              />
              <div className="text-xs text-muted-foreground text-center space-y-1">
                <p>{t("order.find_your_code_on_the_bottle_or_your_previous_order_confirmation")}</p>
                <p className="text-[10px]">
                  <span className="font-semibold">AN</span> {t("order.individual_perfume_code")} <span className="font-semibold">SC</span> {t("order.collection_code")}
                </p>
              </div>
              {error && (
                <p className="text-sm font-medium text-destructive text-center mt-3">
                  {error}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="gold"
              size="lg"
              className="w-full gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("order.finding_your_order")}
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  {t("order.find_my_perfume")}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Direct Collection Product (found by SKU/code) */}
            {directProduct ? (
              <>
                {/* Product Info Header */}
                <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground">{t("order.product_found")}</p>
                    <span className="text-xs px-2 py-0.5 rounded font-mono font-bold bg-primary/10 text-primary">
                      {directProduct.sku || directProduct.perfume_code}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {directProduct.is_signature_box
                      ? t("order.this_is_a_signature_box_confirm_your_reorder_below")
                      : t("order.select_your_preferred_size_and_confirm")}
                  </p>
                </div>

                {/* Product Card */}
                <div className="rounded-xl border-2 border-gold bg-gold/5 p-4">
                  <div className="flex items-start gap-4">
                    {/* Product Image */}
                    {directProduct.images && directProduct.images.length > 0 && (
                      <img 
                        src={(directProduct.images.find(i => i.is_primary) || directProduct.images[0]).image_url}
                        alt={directProduct.name}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display text-base font-semibold text-foreground">{directProduct.name}</h4>
                      <p className="text-xs font-mono text-gold font-medium mt-0.5">
                        {t("order.code")} {directProduct.sku || directProduct.perfume_code}
                      </p>
                      {directProduct.is_signature_box && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium inline-block mt-1">
                          {t("order.signature_box")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Signature Box Contents */}
                  {directProduct.is_signature_box && directProduct.boxItems && directProduct.boxItems.length > 0 && (
                    <div className="mt-3 ps-4 border-l-2 border-gold/30 space-y-1.5">
                      {directProduct.boxItems.map((bp, bpIdx) => (
                        <div key={bpIdx} className="flex items-center gap-1.5 text-xs">
                          <span className="text-foreground">• {bp.perfume_name}</span>
                          {bp.perfume_code && (
                            <span className="font-mono text-gold text-[10px]" dir="ltr">{bp.perfume_code}</span>
                          )}
                          <span className="text-muted-foreground">({bp.size_ml}ml)</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Size Selection (non-box products) */}
                  {!directProduct.is_signature_box && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t("order.select_size")}</p>
                      <div className="flex gap-2">
                        {directProduct.is_50ml_active && (
                          <button
                            onClick={() => setDirectProductSize(50)}
                            className={`flex-1 rounded-lg border-2 py-2 px-3 text-sm font-medium transition-all ${
                              directProductSize === 50
                                ? 'border-gold bg-gold/10 text-foreground'
                                : 'border-border text-muted-foreground hover:border-gold/50'
                            }`}
                          >
                            50ml — AED {directProduct.price_50ml || directProduct.price_aed}
                          </button>
                        )}
                        {directProduct.is_100ml_active && (
                          <button
                            onClick={() => setDirectProductSize(100)}
                            className={`flex-1 rounded-lg border-2 py-2 px-3 text-sm font-medium transition-all ${
                              directProductSize === 100
                                ? 'border-gold bg-gold/10 text-foreground'
                                : 'border-border text-muted-foreground hover:border-gold/50'
                            }`}
                          >
                            100ml — AED {directProduct.price_100ml || directProduct.price_aed}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Selection Header (order-based reorder) */}
                <div className={`border rounded-lg p-4 ${isCollectionOrder ? 'bg-primary/5 border-primary/20' : 'bg-gold/10 border-gold/20'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground">
                      {isCollectionOrder ? t("order.collection_order") : 'Perfume'} {t("order.found")}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded font-mono font-bold ${isCollectionOrder ? 'bg-primary/10 text-primary' : 'bg-gold/20 text-gold'}`}>
                      {foundOrder?.system_perfume_code}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isCollectionOrder 
                      ? `This order contains ${collectionItems.length} item${collectionItems.length > 1 ? 's' : ''}. Select which ones you'd like to reorder.`
                      : isCollectionCode 
                        ? `This collection contains ${orderPerfumes.length} perfume${orderPerfumes.length > 1 ? 's' : ''}. Select one or more to reorder.`
                        : orderPerfumes.length > 1 
                          ? `This order contains ${orderPerfumes.length} perfumes. Select which ones you'd like to reorder.`
                          : t("order.confirm_your_perfume_reorder_below")
                    }
                  </p>
                </div>
              </>
            )}

            {/* Collection Items Selection (order-based) */}
            {!directProduct && isCollectionOrder ? (
              <div className="space-y-3">
                {collectionItems.map((item, index) => {
                  const isSelected = selectedCollectionItemIds.includes(item.productId);
                  const itemCode = item.sku || item.perfumeCode || '';
                  
                  return (
                    <div
                      key={`${item.productId}-${index}`}
                      onClick={() => toggleCollectionItemSelection(item.productId)}
                      className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? "border-gold bg-gold/5 shadow-md" 
                          : "border-border hover:border-gold/50 bg-background"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="pt-1">
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => toggleCollectionItemSelection(item.productId)}
                            className="data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                          />
                        </div>
                        
                        {/* Product Image */}
                        {item.productImage && (
                          <img 
                            src={item.productImage} 
                            alt={item.productName}
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-display text-sm font-semibold text-foreground truncate">
                            {item.productName}
                          </h4>
                          
                          {itemCode && (
                            <p className="text-xs font-mono text-gold font-medium mt-0.5">
                              {t("order.code")} {itemCode}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              {item.sizeMl}ml × {item.quantity}
                            </span>
                            {item.isSignatureBox && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">
                                {t("order.signature_box")}
                              </span>
                            )}
                          </div>
                          
                          {/* Signature Box Contents */}
                          {item.isSignatureBox && item.boxContents && item.boxContents.length > 0 && (
                            <div className="mt-2 ps-2 border-l-2 border-gold/30 space-y-1">
                              {item.boxContents.map((bp, bpIdx) => (
                                <div key={bpIdx} className="flex items-center gap-1.5 text-xs">
                                  <span className="text-foreground">{bp.productName}</span>
                                  {(bp.sku || bp.perfumeCode) && (
                                    <span className="font-mono text-gold text-[10px]">
                                      {bp.sku || bp.perfumeCode}
                                    </span>
                                  )}
                                  <span className="text-muted-foreground">({bp.sizeMl}ml)</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Price */}
                        <div className="text-end flex-shrink-0">
                          <p className="text-sm font-bold text-gold">AED {item.unitPrice}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : !directProduct ? (
              /* Quiz Perfume Selection Cards */
              <div className="space-y-3">
                {orderPerfumes.length === 0 ? (
                  /* Single perfume order without detailed perfume data */
                  <div className="relative rounded-xl border-2 p-4 border-gold bg-gold/5 shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="pt-1">
                        <Checkbox 
                          checked={true}
                          disabled
                          className="data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-gold">1</span>
                          </div>
                          <h4 className="font-display text-base font-semibold text-foreground truncate">
                            {t("order.your_personalized_perfume")}
                          </h4>
                        </div>
                        <p className="text-xs font-mono text-gold font-medium mb-2">
                          {t("order.code")} {foundOrder?.system_perfume_code}
                        </p>
                        {foundOrder?.perfume_description && (
                          <p className="text-xs text-muted-foreground italic">
                            {foundOrder.perfume_description}
                          </p>
                        )}
                      </div>
                      <div className="bg-gold text-primary-foreground p-1 rounded-full">
                        <Check className="h-3 w-3" />
                      </div>
                    </div>
                  </div>
                ) : (
                  orderPerfumes.map((perfume, index) => {
                    const isSelected = selectedPerfumeIds.includes(perfume.id);
                    const perfumeCode = perfume.perfumeCode || perfume.anayaCode || perfume.code;
                    
                    return (
                      <div
                        key={perfume.id}
                        onClick={() => togglePerfumeSelection(perfume.id)}
                        className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all duration-200 ${
                          isSelected 
                            ? "border-gold bg-gold/5 shadow-md" 
                            : "border-border hover:border-gold/50 bg-background"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="pt-1">
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => togglePerfumeSelection(perfume.id)}
                              className="data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-gold">{index + 1}</span>
                              </div>
                              <h4 className="font-display text-base font-semibold text-foreground truncate">
                                {getTypeDisplayLabel(perfume.type)}
                              </h4>
                            </div>
                            {perfumeCode && (
                              <p className="text-xs font-mono text-gold font-medium mb-2">
                                {t("order.code")} {perfumeCode}
                              </p>
                            )}
                            <div className="space-y-1.5 text-xs">
                              {perfume.topNotes && perfume.topNotes.length > 0 && (
                                <div>
                                  <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{t("order.top")} </span>
                                  <span className="text-foreground">{perfume.topNotes.slice(0, 3).join(", ")}</span>
                                </div>
                              )}
                              {(perfume.heartNotes || perfume.middleNotes) && (perfume.heartNotes?.length || perfume.middleNotes?.length) > 0 && (
                                <div>
                                  <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{t("order.heart")} </span>
                                  <span className="text-foreground">
                                    {(perfume.heartNotes || perfume.middleNotes)?.slice(0, 3).join(", ")}
                                  </span>
                                </div>
                              )}
                              {perfume.baseNotes && perfume.baseNotes.length > 0 && (
                                <div>
                                  <span className="text-muted-foreground uppercase tracking-wide text-[10px]">{t("order.base")} </span>
                                  <span className="text-foreground">{perfume.baseNotes.slice(0, 3).join(", ")}</span>
                                </div>
                              )}
                            </div>
                            {perfume.occasion && (
                              <p className="text-xs text-muted-foreground mt-2 italic">
                                {perfume.occasion}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="bg-gold text-primary-foreground p-1 rounded-full">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}

            {/* Price Summary Strip */}
            <div className="bg-amber-800 text-white rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {directProduct
                      ? directProduct.name
                      : isCollectionOrder
                        ? `${selectedCollectionItems.length} item${selectedCollectionItems.length !== 1 ? 's' : ''} selected`
                        : `${effectiveCount} perfume${effectiveCount !== 1 ? 's' : ''} selected`
                    }
                  </p>
                  <p className="text-xs text-white/70">
                    {directProduct
                      ? (directProduct.is_signature_box ? t("order.signature_box") : `${directProductSize}ml`)
                      : isCollectionOrder ? t("order.anaya_collection") : 'AED 199 per perfume'}
                  </p>
                </div>
                <div className="text-end">
                  <p className="text-2xl font-bold">
                    AED {directProduct ? directProductPrice : isCollectionOrder ? collectionTotal : currentPrice}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('code');
                  setFoundOrder(null);
                  setSelectedPerfumeIds([]);
                  setIsCollectionOrder(false);
                  setSelectedCollectionItemIds([]);
                  setDirectProduct(null);
                  setDirectProductSize(100);
                }}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("order.back")}
              </Button>
              <Button
                variant="gold"
                onClick={handleAddSelectedToCart}
                disabled={
                  addingCollectionItems || 
                  (isCollectionOrder ? selectedCollectionItemIds.length === 0 : (orderPerfumes.length > 0 && selectedPerfumeIds.length === 0))
                }
                className="flex-1 gap-2"
              >
                {addingCollectionItems ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("order.adding")}
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-4 w-4" />
                    {t("order.add_to_cart_checkout")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReorderModal;
